/* BP-EP-20260907: independent collection interactions from approved rendered states.
   Product and filter values remain canonical Liquid data; no reference source is used. */
(() => {
  const reducedMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  function showDialog(dialog, opener) {
    if (!dialog || dialog.open) return;
    dialog.fsOpenedBy = opener;
    dialog.showModal();
    document.body.classList.add('fs-collection-modal-open');
    dialog.scrollTop = 0;
    dialog.querySelector('[data-close-dialog]')?.focus({ preventScroll: true });
  }
  function closeDialog(dialog, returnFocus = true) {
    if (!dialog?.open) return;
    dialog.close();
    document.body.classList.remove('fs-collection-modal-open');
    dialog.querySelectorAll('video').forEach(video => video.pause());
    if (returnFocus) dialog.fsOpenedBy?.focus({ preventScroll: true });
  }
  function connectDialog(dialog) {
    dialog.addEventListener('cancel', event => {
      event.preventDefault();
      closeDialog(dialog);
    });
    dialog.addEventListener('click', event => {
      if (event.target !== dialog) return;
      const rect = dialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) closeDialog(dialog);
    });
  }
  class FsCollection extends HTMLElement {
    connectedCallback() {
      if (this.fsConnected) return;
      this.fsConnected = true;
      this.dialog = this.querySelector('.fs-collection__filters');
      this.form = this.querySelector('[data-filter-form]');
      this.sort = this.querySelector('[data-sort-value]');
      if (!this.form || !this.dialog) return;
      connectDialog(this.dialog);
      this.querySelector('[data-open-filters]')?.addEventListener('click', event => {
        this.dialog.querySelectorAll('details').forEach(details => { details.open = false; });
        showDialog(this.dialog, event.currentTarget);
      });
      this.dialog.querySelector('[data-close-dialog]')?.addEventListener('click', () => closeDialog(this.dialog));
      this.querySelector('[data-sort-desktop]')?.addEventListener('change', event => {
        this.sort.value = event.target.value;
        this.submitFilters();
      });
      this.querySelector('[data-sort-mobile]')?.addEventListener('change', event => { this.sort.value = event.target.value; });
      this.form.addEventListener('submit', event => {
        event.preventDefault();
        if (this.form.reportValidity()) this.submitFilters();
      });
      this.form.addEventListener('change', () => this.updateClearButton());
      this.form.addEventListener('input', () => this.updateClearButton());
      this.querySelector('[data-clear-filters]')?.addEventListener('click', () => {
        this.form.querySelectorAll('input[type="checkbox"]').forEach(input => { input.checked = false; });
        this.form.querySelectorAll('[data-price-min],[data-price-max]').forEach(input => { input.value = ''; });
        this.form.querySelectorAll('[data-price-range]').forEach(group => this.syncRange(group));
        this.updateClearButton();
        this.submitFilters();
      });
      this.querySelectorAll('[data-filter-back]').forEach(button => button.addEventListener('click', () => {
        const group = button.closest('details');
        group.open = false;
        group.querySelector('summary')?.focus();
      }));
      this.querySelectorAll('[data-price-range]').forEach(group => {
        const from = group.querySelector('[data-price-min]');
        const to = group.querySelector('[data-price-max]');
        const low = group.querySelector('[data-range-min]');
        const high = group.querySelector('[data-range-max]');
        low.addEventListener('input', () => {
          low.value = Math.min(Number(low.value), Number(high.value));
          from.value = low.value;
          to.min = low.value;
          this.updateClearButton();
        });
        high.addEventListener('input', () => {
          high.value = Math.max(Number(high.value), Number(low.value));
          to.value = high.value;
          from.max = high.value;
          this.updateClearButton();
        });
        from.addEventListener('input', () => this.syncRange(group));
        to.addEventListener('input', () => this.syncRange(group));
      });
    }
    syncRange(group) {
      const from = group.querySelector('[data-price-min]');
      const to = group.querySelector('[data-price-max]');
      const maximum = Number(group.dataset.maxPrice);
      const minimumValue = from.value === '' ? 0 : Number(from.value);
      const maximumValue = to.value === '' ? maximum : Number(to.value);
      group.querySelector('[data-range-min]').value = minimumValue;
      group.querySelector('[data-range-max]').value = maximumValue;
      from.max = maximumValue;
      to.min = minimumValue;
    }
    updateClearButton() {
      const active = Array.from(this.form.querySelectorAll('input[name^="filter."]')).some(input => input.type === 'checkbox' ? input.checked : input.value !== '');
      this.querySelector('[data-clear-filters]').disabled = !active;
    }
    submitFilters() {
      const next = new URL(this.form.action, location.origin);
      const current = new URL(location.href);
      for (const [name, value] of new FormData(this.form)) if (String(value).trim() !== '') next.searchParams.append(name, String(value));
      for (const name of ['preview_theme_id', 'pb']) if (current.searchParams.has(name)) next.searchParams.set(name, current.searchParams.get(name));
      location.assign(next.href);
    }
  }
  class FsCollectionProduct extends HTMLElement {
    connectedCallback() {
      if (this.fsConnected) return;
      this.fsConnected = true;
      this.dialog = this.querySelector('.fs-quickview');
      this.track = this.querySelector('[data-media-track]');
      this.form = this.querySelector('[data-quick-form]');
      this.submit = this.querySelector('[data-quick-submit]');
      this.variantButtons = Array.from(this.querySelectorAll('[data-variant-id]'));
      this.dots = Array.from(this.querySelectorAll('[data-media-index]'));
      this.slides = Array.from(this.querySelectorAll('[data-media-slide]'));
      if (!this.dialog || !this.form) return;
      connectDialog(this.dialog);
      this.querySelector('[data-open-quickview]')?.addEventListener('click', event => {
        showDialog(this.dialog, event.currentTarget);
        requestAnimationFrame(() => this.showMedia(this.mediaIndex || 0, false));
      });
      this.querySelector('[data-close-dialog]')?.addEventListener('click', () => closeDialog(this.dialog));
      this.variantButtons.forEach(button => button.addEventListener('click', () => this.chooseVariant(button)));
      this.querySelectorAll('[data-quick-variant-id]').forEach(button => button.addEventListener('click', () => {
        const source = this.variantButtons.find(variant => variant.dataset.variantId === button.dataset.quickVariantId);
        if (source) this.chooseVariant(source);
      }));
      this.dots.forEach(button => button.addEventListener('click', () => this.showMedia(Number(button.dataset.mediaIndex))));
      this.track?.addEventListener('scroll', () => {
        clearTimeout(this.scrollTimer);
        this.scrollTimer = setTimeout(() => this.updateMedia(Math.round(this.track.scrollLeft / Math.max(this.track.clientWidth, 1))), 80);
      }, { passive: true });
      this.track?.addEventListener('keydown', event => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const index = event.key === 'Home' ? 0 : event.key === 'End' ? this.slides.length - 1 : (this.mediaIndex || 0) + (event.key === 'ArrowRight' ? 1 : -1);
        this.showMedia(index);
      });
      this.querySelectorAll('[data-quantity-change]').forEach(button => button.addEventListener('click', () => {
        const quantity = this.form.elements.quantity;
        if (Number(button.dataset.quantityChange) > 0) quantity.stepUp(); else quantity.stepDown();
        quantity.dispatchEvent(new Event('change', { bubbles: true }));
      }));
      this.form.addEventListener('submit', event => this.addToCart(event));
      this.resizeObserver = new ResizeObserver(() => {
        if (this.dialog.open && this.track?.clientWidth) this.showMedia(this.mediaIndex || 0, false);
      });
      if (this.track) this.resizeObserver.observe(this.track);
    }
    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      clearTimeout(this.scrollTimer);
      if (this.dialog?.open) closeDialog(this.dialog, false);
    }
    chooseVariant(button) {
      const data = button.dataset;
      this.variantButtons.forEach(item => item.setAttribute('aria-pressed', String(item === button)));
      this.querySelectorAll('[data-quick-variant-id]').forEach(item => item.setAttribute('aria-pressed', String(item.dataset.quickVariantId === data.variantId)));
      this.form.elements.id.value = data.variantId;
      this.querySelectorAll('[data-card-price],[data-quick-price]').forEach(price => { price.textContent = data.variantPrice; });
      const available = data.variantAvailable === 'true';
      const stock = this.querySelector('[data-quick-stock]');
      stock.textContent = available ? 'In stock' : 'Sold out';
      stock.toggleAttribute('data-sold-out', !available);
      this.submit.disabled = !available;
      this.submit.querySelector('span').textContent = available ? 'Add to cart' : 'Sold out';
      const quantity = this.form.elements.quantity;
      quantity.min = data.variantMin || '1';
      quantity.step = data.variantStep || '1';
      if (data.variantMax) quantity.max = data.variantMax; else quantity.removeAttribute('max');
      quantity.value = quantity.min;
      this.querySelector('[data-quick-error]').hidden = true;
      this.querySelectorAll('[data-product-link]').forEach(link => {
        const destination = new URL(this.dataset.productUrl, location.origin);
        destination.searchParams.set('variant', data.variantId);
        link.href = destination.pathname + destination.search;
      });
      const image = this.querySelector('[data-card-image]');
      if (image && data.variantImage) {
        image.src = data.variantImage;
        image.removeAttribute('srcset');
        image.alt = `${this.dataset.productTitle} — ${data.variantName}`;
      }
      const index = this.slides.findIndex(slide => slide.dataset.mediaId === data.variantMediaId);
      if (index >= 0) {
        this.mediaIndex = index;
        if (this.dialog.open) this.showMedia(index);
      }
    }
    showMedia(index, smooth = true) {
      if (!this.track || !this.slides.length) return;
      const next = Math.max(0, Math.min(this.slides.length - 1, index));
      this.updateMedia(next);
      this.track.scrollTo({ left: this.track.clientWidth * next, behavior: smooth && !reducedMotion() ? 'smooth' : 'instant' });
    }
    updateMedia(index) {
      this.mediaIndex = index;
      this.dots.forEach((dot, item) => dot.setAttribute('aria-pressed', String(item === index)));
      this.slides.forEach((slide, item) => {
        if (item !== index) slide.querySelectorAll('video').forEach(video => video.pause());
      });
    }
    async addToCart(event) {
      event.preventDefault();
      if (this.busy || !this.form.reportValidity() || this.submit.disabled) return;
      this.busy = true;
      this.submit.setAttribute('aria-busy', 'true');
      this.submit.querySelector('span').textContent = 'Adding…';
      const error = this.querySelector('[data-quick-error]');
      error.hidden = true;
      const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      const data = new FormData(this.form);
      if (cart?.getSectionsToRender) {
        data.append('sections', cart.getSectionsToRender().map(section => section.id).join(','));
        data.append('sections_url', location.pathname);
      }
      try {
        const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', { method: 'POST', headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' }, body: data });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description || result.message || 'Unable to add this product. Please try again.');
        closeDialog(this.dialog, false);
        if (cart?.renderContents) {
          cart.setActiveElement(this.dialog.fsOpenedBy);
          cart.classList.remove('is-empty');
          cart.renderContents(result);
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'fs-collection', productVariantId: data.get('id'), cartData: result });
        } else {
          const next = new URL(window.routes?.cart_url || '/cart', location.origin);
          const current = new URL(location.href);
          for (const key of ['preview_theme_id', 'pb']) if (current.searchParams.has(key)) next.searchParams.set(key, current.searchParams.get(key));
          location.assign(next.href);
        }
      } catch (failure) {
        error.textContent = failure.message || 'Unable to add this product. Please try again.';
        error.hidden = false;
      } finally {
        this.busy = false;
        this.submit.removeAttribute('aria-busy');
        this.submit.querySelector('span').textContent = this.submit.disabled ? 'Sold out' : 'Add to cart';
      }
    }
  }
  if (!customElements.get('fs-collection')) customElements.define('fs-collection', FsCollection);
  if (!customElements.get('fs-collection-product')) customElements.define('fs-collection-product', FsCollectionProduct);
})();
